import React, { useRef, useEffect, useImperativeHandle, forwardRef, useState } from 'react';
import styles from './VideoPlayer.module.scss';

const VideoPlayer = forwardRef(({
    video,
    show,
    onEnded,
    onError,
    onStart,  // NEW: callback when video starts playing
    className,
    posterImage: posterImageProp,
    showSkipButton = false,
    loop = false,  // NEW: loop support for Catchphrase
    muted = false,  // NEW: external mute control
    playing = true  // NEW: external play/pause control
}, ref) => {
    const videoRef = useRef(null);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isPlaying, setIsPlaying] = useState(false);
    const [hasEnded, setHasEnded] = useState(false);
    const [firstFrameLoaded, setFirstFrameLoaded] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [hasStartedOnce, setHasStartedOnce] = useState(false);

    const spinnerTimeoutRef = useRef(null);
    const maxRetries = 3;

    // Parse video prop to get src and posterImage
    const videoSrc = typeof video === 'string' ? video : video?.src;
    const posterImage = (typeof video === 'object' ? video?.posterImage : null) || posterImageProp;

    const handleError = (e) => {
        const vid = videoRef.current;

        if (e instanceof Error) {
            if (e.name === 'NotAllowedError') {
                console.log('Autoplay blocked - video should be muted for TV');
                // For TV, videos should already be muted
                if (vid) {
                    vid.muted = true;
                    vid.play().catch(err => {
                        console.error('Failed to play even when muted:', err);
                        const errorDetails = {
                            code: 5,
                            message: `Play error: ${err.message}`,
                            src: videoSrc,
                        };
                        setError(errorDetails);
                        onError?.(errorDetails);
                    });
                }
                return;
            }

            const errorDetails = {
                code: 5,
                message: `Play error: ${e.message}`,
                src: videoSrc,
            };
            console.error('Video Play Error:', errorDetails);
            setError(errorDetails);
            onError?.(errorDetails);
            return;
        }

        // Handle video element error events
        if (!vid || !videoSrc) return;

        setIsLoading(false);

        const errorDetails = {
            code: vid.error?.code,
            message: getErrorMessage(vid.error?.code),
            src: videoSrc,
            networkState: vid.networkState,
            readyState: vid.readyState
        };

        console.error('Video Error:', errorDetails);
        setError(errorDetails);

        if (retryCount < maxRetries) {
            console.log(`Retrying video load (${retryCount + 1}/${maxRetries})...`);
            setTimeout(() => {
                setRetryCount(prev => prev + 1);
                vid.load();
            }, 1000 * (retryCount + 1));
        } else {
            onError?.(errorDetails);
        }
    };

    const getErrorMessage = (code) => {
        switch (code) {
            case 1: return 'MEDIA_ERR_ABORTED: Video loading was aborted';
            case 2: return 'MEDIA_ERR_NETWORK: Network error occurred';
            case 3: return 'MEDIA_ERR_DECODE: Video decoding failed';
            case 4: return 'MEDIA_ERR_SRC_NOT_SUPPORTED: Video format not supported';
            default: return 'Unknown video error';
        }
    };

    useImperativeHandle(ref, () => ({
        skip: handleSkip,
        getError: () => error,
        isLoading: () => isLoading,
        isPlaying: () => isPlaying,
        play: () => videoRef.current?.play(),
        pause: () => videoRef.current?.pause(),
        restart: () => {
            if (videoRef.current) {
                videoRef.current.currentTime = 0;
                videoRef.current.play().catch(handleError);
            }
        }
    }));

    const handleLoadedMetadata = () => {
        console.log('Video metadata loaded:', videoSrc);
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
        }
    };

    const setFirstFrameReady = () => {
        if (!videoRef.current || firstFrameLoaded) return;

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setFirstFrameLoaded(true);
                if (videoRef.current && show && playing) {
                    videoRef.current.play().catch(handleError);
                }
            });
        });
    };

    // Primary path: fires after seek to currentTime=0
    const handleSeeked = () => setFirstFrameReady();

    // Fallback path: fires when first frame is loaded
    // Covers devices where seeking to 0 from 0 doesn't fire 'seeked'
    const handleLoadedData = () => setFirstFrameReady();

    const handleEnded = () => {
        console.log('Video ended:', videoSrc);
        if (!loop) {
            setHasEnded(true);
            onEnded?.();
        }
        // If loop is true, the video element handles looping automatically
    };

    const handlePlaying = () => {
        console.log('Video playing:', videoSrc);
        clearTimeout(spinnerTimeoutRef.current);
        setIsPlaying(true);
        setIsLoading(false);
        setShowSpinner(false);

        // Fire onStart callback only once per video load
        if (!hasStartedOnce) {
            setHasStartedOnce(true);
            onStart?.();
        }
    };

    const handleWaiting = () => {
        if (!videoSrc) return;
        if (videoRef.current) {
            const timeRemaining = videoRef.current.duration - videoRef.current.currentTime;
            if (timeRemaining <= 0) return;
        }
        setIsLoading(true);
        spinnerTimeoutRef.current = setTimeout(() => setShowSpinner(true), 5000);
    };

    const handleLoadStart = () => {
        if (!videoSrc) return;
        setIsLoading(true);
        setIsPlaying(false);
        setHasStartedOnce(false);
        spinnerTimeoutRef.current = setTimeout(() => setShowSpinner(true), 5000);
    };

    const handleSkip = () => {
        if (videoRef.current?.currentTime) {
            videoRef.current.currentTime = videoRef.current.duration;
        }
        onEnded?.();
    };

    // Handle video source changes
    useEffect(() => {
        if (videoRef.current && videoSrc && show) {
            setError(null);
            setRetryCount(0);
            setIsLoading(true);
            setIsPlaying(false);
            setShowSpinner(false);
            setHasEnded(false);
            setFirstFrameLoaded(false);
            setHasStartedOnce(false);
            clearTimeout(spinnerTimeoutRef.current);
            videoRef.current.load();
        }

        if (videoRef.current && !show) {
            videoRef.current.pause();
        }

        return () => clearTimeout(spinnerTimeoutRef.current);
    }, [videoSrc, show]);

    // Handle external play/pause control
    useEffect(() => {
        if (!videoRef.current || !firstFrameLoaded) return;

        if (playing && show) {
            videoRef.current.play().catch(handleError);
        } else {
            videoRef.current.pause();
        }
    }, [playing, show, firstFrameLoaded]);

    return (
        <div className={`${styles.videoContainer} ${className || ''}`} data-show={show}>
            {posterImage && (
                <div className={styles.posterContainer} data-show={!firstFrameLoaded && show && !hasEnded}>
                    <img src={posterImage} alt="Video loading" className={styles.posterImage} />
                    {showSpinner && <div className={styles.loadingSpinner} />}
                </div>
            )}

            <video
                ref={videoRef}
                className={styles.videoPlayer}
                data-show={show}
                autoPlay
                preload="metadata"
                playsInline
                loop={loop}
                muted={muted}
                onEnded={handleEnded}
                onError={handleError}
                onPlaying={handlePlaying}
                onWaiting={handleWaiting}
                onLoadStart={handleLoadStart}
                onLoadedMetadata={handleLoadedMetadata}
                onLoadedData={handleLoadedData}
                onSeeked={handleSeeked}
                disableremoteplayback
                x-webkit-airplay="deny"
                playsinline
            >
                {videoSrc && <source src={videoSrc} type="video/mp4" />}
            </video>

            {showSkipButton && !loop && (
                <button className={styles.skipButton} data-show={show && !error} onClick={handleSkip}>
                    Skip
                </button>
            )}

            {error && retryCount >= maxRetries && (
                <div className={styles.errorOverlay} data-show={show}>
                    <div className={styles.errorContent}>
                        <h3>Video Error</h3>
                        <p>{error.message}</p>
                        <div className={styles.errorButtons}>
                            <button onClick={() => { setError(null); setRetryCount(0); videoRef.current?.load(); }}>
                                Try Again
                            </button>
                            {!loop && <button onClick={handleSkip}>Skip Video</button>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;